import { useEffect, useState } from "react";
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { SoftButton, cls, useDialogPaperSx } from "../hatim/soft/SoftKit";
import { PACKAGE_TITLE_MAX } from "./questionRules";

/**
 * Sorulardan yeni paket oluşturmak için küçük ad penceresi (mobil
 * SaveAsPackageSheet). onSave başarısızlıkta hata mesajıyla reddeder;
 * pencere açık kalır ve mesajı gösterir.
 */
export default function SaveAsPackageDialog({ open, initialTitle, onClose, onSave }) {
  const { t } = useScriptMode();
  const paperSx = useDialogPaperSx();
  const [title, setTitle] = useState(initialTitle || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setTitle((initialTitle || "").slice(0, PACKAGE_TITLE_MAX));
      setError(null);
    }
    // Yalnız açılışta ön değer alınır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmed = title.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= PACKAGE_TITLE_MAX;

  const submit = async () => {
    if (busy || !valid) return;
    setBusy(true);
    setError(null);
    try {
      await onSave(trimmed);
    } catch (e) {
      setError(e?.message || t("mzq_package_save_failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      maxWidth="xs"
      fullWidth
      sx={{ zIndex: 1500 }}
      PaperProps={{ sx: { borderRadius: 3, ...paperSx } }}
    >
      <DialogTitle sx={{ fontWeight: 500 }}>{t("mzq_save_as_package")}</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-3 pt-1">
          <p className={`text-sm ${cls.muted}`}>{t("mzq_save_as_package_hint")}</p>
          <label className="flex flex-col gap-1.5">
            <span className={`flex justify-between text-xs ${cls.muted}`}>
              <span>{t("mzq_package_title")}</span>
              <span>{`${title.length}/${PACKAGE_TITLE_MAX}`}</span>
            </span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              maxLength={PACKAGE_TITLE_MAX}
              autoFocus
              disabled={busy}
              className={`w-full rounded-xl border border-terakkiBorder bg-terakkiSurface px-3 py-2 text-sm text-terakkiInk outline-none focus:border-terakkiPrimary ${cls.input}`}
            />
          </label>
          {error ? (
            <p role="alert" className={`text-sm ${cls.danger}`}>
              {error}
            </p>
          ) : null}
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <SoftButton tone="cancel" onClick={onClose} disabled={busy}>
          {t("muzakere_cancel")}
        </SoftButton>
        <SoftButton tone="success" onClick={submit} disabled={!valid || busy}>
          {busy ? <CircularProgress size={18} color="inherit" /> : t("save")}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
